import { lazy, Suspense } from 'react'
import { NavLink, Navigate, Route, Routes } from 'react-router-dom'
import { MapPin, PackageCheck, ShoppingBag, ShoppingBasket, Store } from 'lucide-react'
import clsx from 'clsx'
import { useTranslation } from 'react-i18next'
import { useMallCart } from '@/hooks/admin/useMall'
import { useAuthStore } from '@/store/authStore'
import { isLcOwner } from '@/utils/roleUtils'
import { MallLoading, OwnerOnlyNotice } from '@/components/admin/mall/MallUi'

const MallCatalogPage = lazy(() => import('./mall/MallCatalogPage'))
const MallProductDetailPage = lazy(() => import('./mall/MallProductDetailPage'))
const MallCartPage = lazy(() => import('./mall/MallCartPage'))
const MallCheckoutPage = lazy(() => import('./mall/MallCheckoutPage'))
const MallCheckoutCompletePage = lazy(() => import('./mall/MallCheckoutCompletePage'))
const MallOrderListPage = lazy(() => import('./mall/MallOrderListPage'))
const MallOrderDetailPage = lazy(() => import('./mall/MallOrderDetailPage'))
const MallAddressPage = lazy(() => import('./mall/MallAddressPage'))

const navClass = ({ isActive }: { isActive: boolean }) =>
  clsx(
    'inline-flex min-h-10 shrink-0 items-center gap-2 border-b-2 px-1 text-sm font-medium transition-colors',
    isActive
      ? 'border-primary-500 text-primary-700'
      : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-800',
  )

function OwnerOnlyRoute({ children }: { children: React.ReactNode }) {
  const { t } = useTranslation('mall')
  const user = useAuthStore((state) => state.user)

  if (isLcOwner(user ?? undefined)) return <>{children}</>

  return (
    <div className="mx-auto max-w-3xl py-8">
      <OwnerOnlyNotice
        title={t('ownerOnly.actionTitle')}
        description={t('ownerOnly.actionDescription')}
      />
      <NavLink
        to="/admin/mall"
        className="mt-5 inline-flex min-h-10 items-center rounded-lg bg-primary-600 px-4 text-sm font-semibold text-white hover:bg-primary-700"
      >
        {t('ownerOnly.backToCatalog')}
      </NavLink>
    </div>
  )
}

export default function MallPage() {
  const { t } = useTranslation('mall')
  const user = useAuthStore((state) => state.user)
  const isOwner = isLcOwner(user ?? undefined)
  const { data: cart } = useMallCart(isOwner)

  return (
    <div className="space-y-4 py-4">
      <div>
        <div className="flex items-start gap-3">
          <span className="grid size-10 shrink-0 place-items-center rounded-lg bg-primary-50 text-primary-600">
            <ShoppingBag className="size-5" />
          </span>
          <div>
            <h1 className="text-lg font-semibold text-gray-900">{t('brand.name')}</h1>
            <p className="mt-0.5 text-sm text-gray-500">{t('brand.description')}</p>
          </div>
        </div>

        {!isOwner ? (
          <div className="mt-4">
            <OwnerOnlyNotice
              title={t('ownerOnly.title')}
              description={t('ownerOnly.description')}
              compact
            />
          </div>
        ) : null}

        <nav className="mt-4 flex gap-6 overflow-x-auto border-b border-gray-200" aria-label={t('brand.name')}>
          <NavLink to="/admin/mall" end className={navClass}>
            <Store className="size-4" />
            {t('nav.catalog')}
          </NavLink>
          <NavLink to="/admin/mall/orders" className={navClass}>
            <PackageCheck className="size-4" />
            {t('nav.orders')}
          </NavLink>
          {isOwner ? (
            <>
              <NavLink to="/admin/mall/addresses" className={navClass}>
                <MapPin className="size-4" />
                {t('nav.addresses')}
              </NavLink>
              <NavLink to="/admin/mall/cart" className={navClass}>
                <ShoppingBasket className="size-4" />
                <span>{t('nav.cart')}</span>
                {(cart?.totalQuantity ?? 0) > 0 ? (
                  <span
                    className="grid min-w-5 place-items-center rounded-full bg-primary-600 px-1.5 py-0.5 text-[10px] font-bold text-white"
                    aria-label={t('nav.cartCount', { count: cart?.totalQuantity ?? 0 })}
                  >
                    {cart?.totalQuantity}
                  </span>
                ) : null}
              </NavLink>
            </>
          ) : null}
        </nav>
      </div>

      <Suspense fallback={<MallLoading label={t('common.loading')} />}>
        <Routes>
          <Route index element={<MallCatalogPage />} />
          <Route path="products/:productId" element={<MallProductDetailPage />} />
          <Route path="orders" element={<MallOrderListPage />} />
          <Route path="orders/:orderId" element={<MallOrderDetailPage />} />
          <Route path="cart" element={<OwnerOnlyRoute><MallCartPage /></OwnerOnlyRoute>} />
          <Route path="checkout" element={<OwnerOnlyRoute><MallCheckoutPage /></OwnerOnlyRoute>} />
          <Route path="complete/:orderId" element={<OwnerOnlyRoute><MallCheckoutCompletePage /></OwnerOnlyRoute>} />
          <Route path="addresses" element={<OwnerOnlyRoute><MallAddressPage /></OwnerOnlyRoute>} />
          <Route path="*" element={<Navigate to="/admin/mall" replace />} />
        </Routes>
      </Suspense>
    </div>
  )
}
